import { useState, useCallback, useRef } from 'react';
import { ChatRequest, Message } from 'types';
import { supabase } from 'config/supabase';
// @ts-ignore - sse.js types
import { SSE } from 'sse.js';

// SSE Event types (matching backend)
interface DeltaEvent {
  type: 'delta';
  text: string;
}

interface CompleteEvent {
  type: 'complete';
  message_id: string;
  conversation_id: string;
  agent_type: string | null;
  content: Record<string, unknown>;
}

interface ErrorEvent {
  type: 'error';
  message: string;
  code?: string;
}

type SSEEvent = DeltaEvent | CompleteEvent | ErrorEvent;

export function useAgentStream() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const eventSourceRef = useRef<any>(null);

  /**
   * Start streaming a message using SSE.js library
   */
  const startStream = useCallback(
    async (request: ChatRequest, onComplete: (message: Message) => void): Promise<void> => {
      try {
        setIsStreaming(true);
        setStreamingContent('');
        setError(null);

        // Get auth token
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();
        if (sessionError || !session) {
          throw new Error('User not authenticated');
        }

        const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/chat-orchestrator`;

        // Create SSE connection with POST support
        const eventSource = new SSE(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          payload: JSON.stringify({
            ...request,
            stream: true, // Enable streaming
          }),
        });

        // Store ref for cleanup
        eventSourceRef.current = eventSource;

        // Track accumulated content in closure
        let accumulatedContent = '';

        // Handle incoming messages
        eventSource.addEventListener('message', (e: any) => {
          console.log(111, e);
          try {
            const event: SSEEvent = JSON.parse(e.data);

            if (event.type === 'delta') {
              // Append text chunk
              accumulatedContent += event.text;
              setStreamingContent(accumulatedContent);
              console.log('[Stream] Received delta:', event.text.length, 'chars');
            } else if (event.type === 'complete') {
              // Stream completed
              console.log('[Stream] Received complete event:', event);

              // Extract message and agent_output from content
              const contentObj = event.content || {};
              const messageText = contentObj.message || accumulatedContent;
              const agentOutput = contentObj.agent_output || null;

              // Transform to Message format
              const message: Message = {
                id: event.message_id,
                conversation_id: event.conversation_id,
                role: 'assistant',
                // @ts-ignore
                content: event.agent_type
                  ? {
                      message: messageText, // The main message text
                      agent_output: agentOutput, // The agent output data
                      response_type: event.content?.response_type,
                    }
                  : messageText, // For conversational responses, use plain text

                agent_type: event.agent_type as any,
                agent_output: agentOutput, // Keep for backward compatibility
                created_at: new Date().toISOString(),
              };

              // Close the stream
              eventSource.close();
              eventSourceRef.current = null;

              // Call completion callback
              onComplete(message);

              // Reset streaming state
              setIsStreaming(false);

              // Focus back on input after stream completes
              setTimeout(() => {
                const textarea = document.querySelector(
                  'textarea[placeholder="Describe what you need..."]',
                );
                if (textarea) {
                  (textarea as HTMLTextAreaElement).focus();
                }
              }, 100);
            } else if (event.type === 'error') {
              // Error occurred
              console.error('[Stream] Error event:', event);
              setError(event.message);
              eventSource.close();
              eventSourceRef.current = null;
              setIsStreaming(false);
            }
          } catch (parseError) {
            console.error('[Stream] Error parsing event data:', parseError);
            setError('Failed to parse streaming response');
            eventSource.close();
            eventSourceRef.current = null;
            setIsStreaming(false);
          }
        });

        // Handle errors
        eventSource.addEventListener('error', (err: any) => {
          console.error('[Stream] SSE error:', err);
          setError('Streaming connection error');
          eventSource.close();
          eventSourceRef.current = null;
          setIsStreaming(false);
        });

        // Start the stream
        eventSource.stream();
      } catch (err) {
        console.error('[Stream] Stream error:', err);
        setError(err instanceof Error ? err.message : 'Unknown streaming error');
        setIsStreaming(false);
      }
    },
    [],
  );

  /**
   * Cancel ongoing stream
   */
  const cancelStream = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('[Stream] Cancelling stream...');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsStreaming(false);
    setStreamingContent('');
    setError(null);
  }, []);

  /**
   * Reset stream state
   */
  const resetStream = useCallback(() => {
    setStreamingContent('');
    setError(null);
    setIsStreaming(false);
  }, []);

  return {
    isStreaming,
    streamingContent,
    error,
    startStream,
    cancelStream,
    resetStream,
  };
}
